/**
 * ObjectDetailLayout — der Objekt-Arbeitsraum.
 *
 * Kopf mit Zustand und seltenen Aktionen, daneben eine gruppierte Bereichsleiste mit dem Stand je
 * Bereich, rechts die Seite des Bereichs. Eine Reiterzeile trug weder Zustand noch Gruppen und
 * wäre mit jedem weiteren Bereich übergelaufen.
 */
import { Outlet, useParams, useRouterState } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { EmptyState } from '@/shared/components';
import { useApi } from '@/shared/api';
import { canManage, useAuth } from '@/shared/auth';
import { ObjectDetailContext } from './ObjectDetailContext';
import { ObjectAreaNavigation } from './ObjectAreaNavigation';
import { ObjectHeader } from './ObjectHeader';
import { activeObjectArea, visibleObjectAreas } from './objectAreas';
import { objectAreaHints } from './objectAreaStatus';
import { objectPublicationBadge } from './objectPublicationBadge';
import { useObjectStatusData } from './useObjectStatusData';
import type { PropertyGroupResponse } from '@/shared/api';

export { useObjectDetail } from './ObjectDetailContext';

export function ObjectDetailLayout() {
  const { id } = useParams({ from: '/_admin/property-groups/$id' });
  const api = useApi();
  const user = useAuth().user;
  const canEdit = canManage(user);

  const {
    data: group,
    error,
    isLoading,
  } = useQuery({
    queryKey: ['property-groups', id],
    queryFn: () => api.propertyGroups.get(id),
  });

  // Ein fehlgeschlagenes Nachladen ersetzt nicht den Arbeitsraum, der schon dasteht.
  if (error && !group) {
    return (
      <EmptyState
        icon="Error"
        title="Objekt konnte nicht geladen werden"
        description="Bitte erneut versuchen."
      />
    );
  }
  if (isLoading || !group) return null;

  return <ObjectWorkspace group={group} canEdit={canEdit} />;
}

function ObjectWorkspace({ group, canEdit }: { group: PropertyGroupResponse; canEdit: boolean }) {
  const user = useAuth().user;
  const pathname = useRouterState().location.pathname;
  const areas = visibleObjectAreas(user, canEdit);
  const active = activeObjectArea(areas, group.id, pathname);
  const status = useObjectStatusData(group, areas);
  const hints = objectAreaHints(status);
  const badge = objectPublicationBadge(
    group,
    status.publication,
    areas.some((area) => area.id === 'veroeffentlichung'),
  );

  return (
    <div className="@container mx-auto w-full max-w-[1680px]">
      <ObjectHeader group={group} canEdit={canEdit} badge={badge} />
      <div className="@4xl:grid-cols-[200px_minmax(0,1fr)] @4xl:gap-8 grid grid-cols-1 gap-5">
        <ObjectAreaNavigation groupId={group.id} areas={areas} active={active} hints={hints} />
        <div className="min-w-0">
          <ObjectDetailContext.Provider value={{ groupId: group.id, canEdit }}>
            <Outlet />
          </ObjectDetailContext.Provider>
        </div>
      </div>
    </div>
  );
}
